import { useEffect, useMemo, useState } from 'react'
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { api } from '../../../lib/api'
import { Button, ErrorBanner, SegmentedControl, StatTile, StatusChip, chartAxis, chartGrid, chartTooltip, seriesColor, type StatusTone } from '../../../components/ui'
import { useDarkWebPage } from '../DarkWebContext'
import { AIInsightsPanel } from '../AIInsightsPanel'
import { SOURCE_TYPES } from '../format'
import type { PageTab } from '../types'

type Trend = Awaited<ReturnType<typeof api.getDarkWebExposureTrend>>
const SEVERITY_ORDER: Record<string, number> = { critical: 0, serious: 1, warning: 2 }

interface ActionItem {
  key: string
  tone: StatusTone
  severity: string
  title: string
  detail: string
  count: number
  cta: string
  onClick: () => void
}

/**
 * Dark Web overview (#397 step 3d): monochrome key figures, a ranked "Needs action" list,
 * new exposures per month by source, the AI summary, and exposure by source.
 */
export function OverviewTab() {
  const { dashboard, credentialStats, openPeople, setPageTab, dataVersion } = useDarkWebPage()
  const [range, setRange] = useState<'6' | '12'>('6')
  const [trend, setTrend] = useState<Trend | null>(null)
  const [trendError, setTrendError] = useState<string | null>(null)
  const [showTable, setShowTable] = useState(false)
  const [lookalike, setLookalike] = useState<{ total: number; highRisk: number } | null>(null)

  async function loadTrend() {
    setTrendError(null)
    try {
      const res = await api.getDarkWebExposureTrend({ category: 'employee', months: Number(range) })
      if (res.success) setTrend(res)
      else setTrendError(res.error || 'The server returned an error.')
    } catch (e) {
      setTrendError(e instanceof Error ? e.message : 'Network error.')
    }
  }

  useEffect(() => {
    loadTrend()
  }, [range, dataVersion])

  // Lookalike domains are cached server-side for 12 hours, so this doesn't spend WhiteIntel calls
  useEffect(() => {
    api.getDarkWebLookalikeDomains({ limit: 100 })
      .then(res => {
        if (res.success) setLookalike({ total: res.summary?.totalDetected || 0, highRisk: res.summary?.highRisk || 0 })
      })
      .catch(() => setLookalike(null))
  }, [dataVersion])

  const openByType = useMemo(() => {
    const map: Record<string, number> = {}
    for (const row of trend?.bySourceType || []) map[row.source_type] = row.people
    return map
  }, [trend])
  const stealerPeople = openByType.stealer_log || 0
  const exposedEmployees = credentialStats?.exposed_employees || 0
  const exposedCustomers = credentialStats?.exposed_customers || 0
  const withPassword = credentialStats?.exposed_with_password || 0

  const actions: ActionItem[] = [
    stealerPeople > 0 && {
      key: 'stealer', tone: 'critical' as StatusTone, severity: 'critical', count: stealerPeople,
      title: `${stealerPeople.toLocaleString()} employee${stealerPeople === 1 ? '' : 's'} in info-stealer logs`,
      detail: 'Logs carry saved browser passwords and live session cookies, so a password reset alone is not enough: revoke sessions and check the device.',
      cta: 'Review people', onClick: () => openPeople({ sourceType: 'stealer_log' }),
    },
    withPassword > 0 && {
      key: 'password', tone: 'serious' as StatusTone, severity: 'serious', count: withPassword,
      title: `${withPassword.toLocaleString()} ${withPassword === 1 ? 'person has' : 'people have'} an exposed password`,
      detail: 'Credentials from breaches and combolists are reused in credential-stuffing attacks. Force a reset and confirm MFA.',
      cta: 'Review people', onClick: () => openPeople({}),
    },
    (lookalike?.highRisk || 0) > 0 && {
      key: 'lookalike', tone: 'serious' as StatusTone, severity: 'serious', count: lookalike!.highRisk,
      title: `${lookalike!.highRisk} high-risk lookalike domain${lookalike!.highRisk === 1 ? '' : 's'}`,
      detail: 'Typosquatted domains are commonly used for phishing your employees and customers.',
      cta: 'Review domains', onClick: () => setPageTab('threats' as PageTab),
    },
    exposedCustomers > 0 && {
      key: 'customers', tone: 'warning' as StatusTone, severity: 'warning', count: exposedCustomers,
      title: `${exposedCustomers.toLocaleString()} customer account${exposedCustomers === 1 ? '' : 's'} exposed`,
      detail: 'Customer logins for your services appear in leaks. Consider prompting affected customers to change passwords.',
      cta: 'Review customers', onClick: () => openPeople({}, 'customer'),
    },
  ].filter(Boolean) as ActionItem[]
  actions.sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity] || b.count - a.count)

  const chartData = (trend?.months || []).map(m => ({
    ...m,
    label: new Date(`${m.month}-01T00:00:00Z`).toLocaleDateString(undefined, { month: 'short', timeZone: 'UTC' }),
  }))
  const peak = chartData.reduce((max, m) => Math.max(max, m.breach + m.stealer_log + m.combolist), 0)
  const openTotal = SOURCE_TYPES.reduce((sum, s) => sum + (openByType[s.key] || 0), 0)
  const families = dashboard?.aggregates.topStealerFamilies || []
  const maxFamily = families.reduce((max, f) => Math.max(max, f.count), 0)

  return (
    <div className="flex flex-col gap-5">
      <section aria-label="Key figures" className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatTile label="People with open exposures" value={(credentialStats?.exposed_identities ?? 0).toLocaleString()} detail={`${exposedEmployees.toLocaleString()} employees · ${exposedCustomers.toLocaleString()} customers`} />
        <StatTile
          label="Employees in stealer logs"
          value={stealerPeople.toLocaleString()}
          detail="Passwords and session cookies"
          status={stealerPeople > 0 ? <StatusChip tone="critical">Critical</StatusChip> : undefined}
        />
        <StatTile label="Breaches" value={(dashboard?.breakdown.uniqueBreaches ?? 0).toLocaleString()} detail={`${(dashboard?.breakdown.hibpBreachedEmails ?? 0).toLocaleString()} employee emails affected`} />
        <StatTile label="Lookalike domains" value={lookalike ? lookalike.total.toLocaleString() : '—'} detail={lookalike ? `${lookalike.highRisk} high risk` : 'Loading…'} />
      </section>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_380px]">
        <div className="flex min-w-0 flex-col gap-5">
          <section aria-labelledby="dw-na-title" className="rounded-xl border border-line bg-surface">
            <div className="flex flex-col gap-0.5 border-b border-line px-5 py-4">
              <h2 id="dw-na-title" className="m-0 font-heading text-base font-semibold">Needs action</h2>
              <span className="text-[13px] text-ink-secondary">Ranked by severity, then by number of people affected</span>
            </div>
            {actions.length === 0 ? (
              <p className="m-0 px-5 py-6 text-sm text-ink-secondary">Nothing needs action right now.</p>
            ) : (
              <ul className="m-0 list-none p-0">
                {actions.map((action, index) => (
                  <li key={action.key} className="flex flex-wrap items-start gap-4 border-b border-line px-5 py-4 last:border-b-0">
                    <div className="w-[88px] flex-none pt-0.5">
                      <StatusChip tone={action.tone}>{action.severity === 'critical' ? 'Critical' : action.severity === 'serious' ? 'Serious' : 'Warning'}</StatusChip>
                    </div>
                    <div className="flex min-w-[220px] flex-1 flex-col gap-1">
                      <span className="font-semibold">{action.title}</span>
                      <span className="text-[13px] text-ink-secondary">{action.detail}</span>
                    </div>
                    <Button size="sm" variant={index === 0 ? 'primary' : 'secondary'} onClick={action.onClick}>{action.cta}</Button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section aria-labelledby="dw-chart-title" className="flex flex-col gap-4 rounded-xl border border-line bg-surface p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex flex-col gap-0.5">
                <h2 id="dw-chart-title" className="m-0 font-heading text-base font-semibold">New exposures per month</h2>
                <span className="text-[13px] text-ink-secondary">
                  Employees by source and latest leak date ·{' '}
                  <button type="button" onClick={() => setShowTable(v => !v)} className="text-link hover:underline focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-focus rounded">
                    {showTable ? 'View as chart' : 'View as table'}
                  </button>
                </span>
              </div>
              <SegmentedControl label="Time range" value={range} onChange={setRange} options={[{ value: '6', label: '6m' }, { value: '12', label: '1y' }]} />
            </div>
            {trendError && <ErrorBanner title="Couldn't load the trend" description={trendError} onRetry={loadTrend} />}
            {!trendError && (
              <>
                <div className="flex flex-wrap gap-4 text-[13px] text-ink-secondary">
                  {SOURCE_TYPES.map(s => (
                    <span key={s.key} className="inline-flex items-center gap-1.5"><span className={`h-2.5 w-2.5 rounded-sm ${s.color}`} aria-hidden="true" />{s.label}</span>
                  ))}
                </div>
                {showTable ? (
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse text-[13px]">
                      <caption className="sr-only">New exposures per month by source</caption>
                      <thead>
                        <tr className="text-left text-xs font-semibold uppercase tracking-[0.04em] text-muted">
                          <th scope="col" className="border-b border-line py-2 pr-4">Month</th>
                          {SOURCE_TYPES.map(s => <th key={s.key} scope="col" className="border-b border-line py-2 pr-4 text-right">{s.label}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {chartData.map(m => (
                          <tr key={m.month}>
                            <th scope="row" className="border-b border-line py-2 pr-4 text-left font-medium">{m.label} {m.month.slice(0, 4)}</th>
                            {SOURCE_TYPES.map(s => <td key={s.key} className="border-b border-line py-2 pr-4 text-right tabular-nums">{m[s.key as 'breach']}</td>)}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="h-60" role="img" aria-label={`Stacked bar chart of employees newly exposed per month over the last ${range} months; peak month has ${peak}.`}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                        <CartesianGrid {...chartGrid} />
                        <XAxis dataKey="label" {...chartAxis} />
                        <YAxis allowDecimals={false} {...chartAxis} />
                        <Tooltip {...chartTooltip} />
                        {SOURCE_TYPES.map(s => (
                          <Bar key={s.key} dataKey={s.key} name={s.label} stackId="exposures" fill={seriesColor(s.series)} maxBarSize={48} />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </>
            )}
          </section>
        </div>

        <div className="flex min-w-0 flex-col gap-5">
          <AIInsightsPanel page="/dark-web" />

          <section aria-labelledby="dw-src-title" className="flex flex-col gap-3 rounded-xl border border-line bg-surface p-5">
            <div className="flex flex-col gap-0.5">
              <h2 id="dw-src-title" className="m-0 font-heading text-base font-semibold">Exposure by source</h2>
              <span className="text-[13px] text-ink-secondary">Employees with open exposures, by where they were found</span>
            </div>
            {SOURCE_TYPES.map(s => {
              const value = openByType[s.key] || 0
              const pct = openTotal ? Math.round((value / openTotal) * 100) : 0
              return (
                <div key={s.key} className="flex flex-col gap-1.5">
                  <div className="flex justify-between text-[13px]">
                    <span className="inline-flex items-center gap-1.5"><span className={`h-2.5 w-2.5 rounded-sm ${s.color}`} aria-hidden="true" />{s.label}</span>
                    <span className="tabular-nums font-semibold">{value.toLocaleString()} <span className="font-normal text-muted">· {pct}%</span></span>
                  </div>
                  <div className="h-2 overflow-hidden rounded bg-surface-raised" aria-hidden="true">
                    <div className={`h-full ${s.color}`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              )
            })}
            <span className="text-xs text-muted">One person can appear under more than one source.</span>
          </section>

          {families.length > 0 && (
            <section aria-labelledby="dw-mal-title" className="flex flex-col gap-3 rounded-xl border border-line bg-surface p-5">
              <div className="flex flex-col gap-0.5">
                <h2 id="dw-mal-title" className="m-0 font-heading text-base font-semibold">Malware families</h2>
                <span className="text-[13px] text-ink-secondary">Stealers found on employee devices</span>
              </div>
              {families.slice(0, 6).map(f => (
                <div key={f.name} className="grid grid-cols-[100px_1fr_auto] items-center gap-3 text-[13px]">
                  <span className="truncate font-medium">{f.name}</span>
                  <div className="h-2 overflow-hidden rounded bg-surface-raised" aria-hidden="true">
                    <div className="h-full bg-ink-secondary" style={{ width: `${maxFamily ? (f.count / maxFamily) * 100 : 0}%` }} />
                  </div>
                  <span className="tabular-nums text-ink-secondary">{f.count}</span>
                </div>
              ))}
            </section>
          )}
        </div>
      </div>
    </div>
  )
}
